<template>
    <div class="p-6">
        <!-- Header -->
        <div class="mb-6">
            <h1 class="text-3xl font-bold text-rose-800 mb-2">Jurnalul de Activitate</h1>
            <p class="text-gray-600">Istoric complet al tuturor modificărilor în sistem</p>
        </div>

        <!-- Filters -->
        <div class="mb-6 bg-white p-4 rounded-lg shadow">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- Entity Filter -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Tip entitate</label>
                    <Select v-model="filters.entity" :options="entityOptions" optionLabel="label"
                        optionValue="value" placeholder="Toate" class="w-full" showClear />
                </div>

                <!-- Action Filter -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Acțiune</label>
                    <Select v-model="filters.action" :options="actionOptions" optionLabel="label"
                        optionValue="value" placeholder="Toate" class="w-full" showClear />
                </div>

                <!-- Max Results -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Număr rezultate</label>
                    <Select v-model="maxResults" :options="[10, 25, 50, 100]" placeholder="25" class="w-full" showClear />
                </div>
            </div>

            <div class="mt-4 flex gap-2">
                <Button label="Aplică filtre" icon="pi pi-filter" @click="loadLogs" severity="info" />
                <Button label="Resetează" icon="pi pi-refresh" @click="resetFilters" severity="secondary" outlined />
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
            <ProgressSpinner />
            <p class="mt-4 text-gray-600">Se încarcă datele...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            <i class="pi pi-exclamation-triangle mr-2"></i>
            {{ error }}
        </div>

        <!-- Logs List -->
        <div v-if="!loading && logs.length > 0" class="space-y-3">
            <div v-for="log in logs" :key="log.id"
                class="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow">
                <div class="flex items-start justify-between">
                    <!-- Main Info -->
                    <div class="flex-1">
                        <div class="flex items-center gap-3 mb-2">
                            <!-- Action Badge -->
                            <Badge :value="getActionLabel(log.action)" :severity="getActionSeverity(log.action)" />

                            <!-- Entity Badge -->
                            <Badge :value="getEntityLabel(log.entity)" severity="info" />

                            <!-- Timestamp -->
                            <span class="text-sm text-gray-500">
                                <i class="pi pi-clock mr-1"></i>
                                {{ formatDate(log.createdAt) }}
                            </span>
                        </div>

                        <!-- Actor Info -->
                        <div class="text-sm text-gray-700 mb-2">
                            <i class="pi pi-user mr-1"></i>
                            <span class="font-medium">{{ log.actorEmail }}</span>
                        </div>

                        <!-- Metadata (if exists) -->
                        <div v-if="log.metadata && Object.keys(log.metadata).length > 0" class="text-sm text-gray-600">
                            <div v-if="log.metadata.lastName && log.metadata.firstName">
                                <i class="pi pi-users mr-1"></i>
                                Pacient: {{ log.metadata.lastName }} {{ log.metadata.firstName }}
                            </div>
                            <div v-if="log.metadata.patientId">
                                <i class="pi pi-id-card mr-1"></i>
                                CNP: {{ log.metadata.patientId }}
                            </div>
                            <div v-if="log.metadata.dateConsult">
                                <i class="pi pi-calendar mr-1"></i>
                                Data consultație: {{ log.metadata.dateConsult }}
                            </div>
                            <div v-if="log.metadata.fileName">
                                <i class="pi pi-file mr-1"></i>
                                Fișier: {{ log.metadata.fileName }} ({{ log.metadata.type }})
                            </div>
                            <div v-if="log.metadata.examId">
                                <i class="pi pi-file-edit mr-1"></i>
                                ID Examinare: {{ log.metadata.examId }}
                            </div>
                        </div>

                        <!-- Changes (if exists) -->
                        <div v-if="log.changes && Object.keys(log.changes).length > 0" class="mt-2">
                            <button @click="toggleChanges(log.id)"
                                class="text-rose-600 hover:text-rose-800 font-medium text-sm flex items-center gap-1">
                                <i
                                    :class="expandedLogs.has(log.id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"></i>
                                {{ expandedLogs.has(log.id) ? 'Ascunde modificări' : 'Vezi modificări' }}
                            </button>

                            <div v-if="expandedLogs.has(log.id)" class="mt-2 bg-gray-50 p-3 rounded text-sm">
                                <div v-for="(value, field) in log.changes" :key="field" class="mb-2 last:mb-0">
                                    <span class="font-medium text-gray-700">{{ field }}:</span>
                                    <div class="ml-4 mt-1">
                                        <div class="text-red-600">
                                            <i class="pi pi-minus-circle mr-1"></i>
                                            Vechi: {{ value[0] || '(gol)' }}
                                        </div>
                                        <div class="text-green-600">
                                            <i class="pi pi-plus-circle mr-1"></i>
                                            Nou: {{ value[1] || '(gol)' }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Entity ID -->
                    <div class="text-right">
                        <div class="text-xs text-gray-500">ID Entitate:</div>
                        <div class="text-xs font-mono text-gray-700">{{ log.entityId }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && logs.length === 0" class="text-center py-12 bg-gray-50 rounded-lg">
            <i class="pi pi-inbox text-6xl text-gray-400"></i>
            <p class="mt-4 text-gray-600 text-lg">Nu există înregistrări</p>
            <p class="text-gray-500 text-sm">Modificările vor apărea aici pe măsură ce utilizați sistemul</p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Select from 'primevue/select'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import ProgressSpinner from 'primevue/progressspinner'
import { useAuditLog } from '../../composables/useAuditLog.js'

const { getRecentLogs, getActionLabel } = useAuditLog()

const logs = ref([])
const loading = ref(false)
const error = ref(null)
const maxResults = ref(25)
const expandedLogs = ref(new Set())

const filters = ref({
    entity: null,
    action: null
})

const entityOptions = [
    { label: 'Pacient', value: 'patient' },
    { label: 'Examinare', value: 'exam' }
]

const actionOptions = [
    { label: 'Creare pacient', value: 'create_patient' },
    { label: 'Actualizare pacient', value: 'update_patient' },
    { label: 'Creare examinare', value: 'create_exam' },
    { label: 'Actualizare examinare', value: 'update_exam' },
    { label: 'Finalizare examinare', value: 'finalize_exam' },
    { label: 'Anulare examinare', value: 'void_exam' },
    { label: 'Export PDF', value: 'export_pdf' }
]

const loadLogs = async () => {
    loading.value = true
    error.value = null
    
    try {
        const allLogs = await getRecentLogs(maxResults.value)
        
        // Apply filters
        logs.value = allLogs.filter(log => {
            if (filters.value.entity && log.entity !== filters.value.entity) {
                return false
            }
            if (filters.value.action && log.action !== filters.value.action) {
                return false
            }
            return true
        })
    } catch (err) {
        error.value = err.message
        console.error('Error loading logs:', err)
    } finally {
        loading.value = false
    }
}

const resetFilters = () => {
    filters.value = {
        entity: null,
        action: null
    }
    maxResults.value = 25
    loadLogs()
}

const toggleChanges = (logId) => {
    if (expandedLogs.value.has(logId)) {
        expandedLogs.value.delete(logId)
    } else {
        expandedLogs.value.add(logId)
    }
}

const getEntityLabel = (entity) => {
    const labels = {
        patient: 'Pacient',
        exam: 'Examinare'
    }
    return labels[entity] || entity
}

const getActionSeverity = (action) => {
    if (action.includes('create')) return 'success'
    if (action.includes('update')) return 'info'
    if (action.includes('delete') || action.includes('void')) return 'danger'
    if (action.includes('finalize')) return 'success'
    if (action.includes('upload')) return 'info'
    return 'secondary'
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
}

onMounted(() => {
    loadLogs()
})
</script>
